import { useEffect, useRef, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";

const linkStyle = ({ isActive }) => ({
  color: isActive ? "#1E1968" : "#5a6b85",
  fontWeight: isActive ? 500 : 400,
  fontSize: "0.86rem",
  lineHeight: 1.2,
  letterSpacing: "0.005em",
  paddingBottom: 4,
  borderBottom: isActive ? "2px solid #258FC4" : "2px solid transparent",
  transition: "color 0.15s ease, border-color 0.15s ease",
});

// Guides trigger mirrors linkStyle exactly, in a flex row so the chevron sits
// on the same visual baseline as the text of the sibling nav links.
const guidesTriggerStyle = {
  color: "#5a6b85",
  fontFamily: "inherit",
  fontWeight: 400,
  fontSize: "0.86rem",
  lineHeight: 1.2,
  letterSpacing: "0.005em",
  paddingBottom: 4,
  borderBottom: "2px solid transparent",
  background: "transparent",
  border: 0,
  cursor: "pointer",
  transform: "translateY(-3px)",
};

const mobileLinkClass = "block px-5 py-3 text-[0.95rem]";
const mobileLinkStyle = { color: "#17233D", borderBottom: "1px solid #eef2f7" };
const mobileSubLinkStyle = { color: "#3a4d70", borderBottom: "1px solid #eef2f7" };

const GUIDES = [
  { to: "/how-the-score-works", label: "How Your Score Is Calculated", testId: "nav-guide-score" },
  { to: "/financial-planning-20s-30s", label: "Financial Planning in Your 20s & 30s", testId: "nav-guide-20s30s" },
  { to: "/financial-planning-40s-50s", label: "Financial Planning in Your 40s & 50s", testId: "nav-guide-40s50s" },
  { to: "/retirement-readiness-guide", label: "Retirement Readiness Guide", testId: "nav-guide-retirement" },
];

export const NeutralHeader = ({ showSubtitle = true, showNav = true }) => {
  const [open, setOpen] = useState(false);
  const [guidesOpen, setGuidesOpen] = useState(false); // desktop dropdown
  const [mGuidesOpen, setMGuidesOpen] = useState(false); // mobile accordion
  const location = useLocation();
  const guidesRootRef = useRef(null);
  const guidesButtonRef = useRef(null);

  // Close all menus whenever the route changes.
  useEffect(() => {
    setOpen(false);
    setGuidesOpen(false);
    setMGuidesOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the mobile menu is open so the page doesn't drift.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = open ? "hidden" : prev || "";
    return () => { document.body.style.overflow = prev || ""; };
  }, [open]);

  // Desktop Guides: close on outside click + Escape (and return focus to the button on Escape).
  useEffect(() => {
    if (!guidesOpen) return;
    const onClick = (e) => {
      if (guidesRootRef.current && !guidesRootRef.current.contains(e.target)) {
        setGuidesOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setGuidesOpen(false);
        guidesButtonRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [guidesOpen]);

  return (
    <header className="w-full border-b" style={{ background: "#ffffff", borderColor: "#e6edf5" }} data-testid="neutral-header">
      <a
        href="#main-content"
        className="skip-link"
        data-testid="skip-to-main"
      >
        Skip to main content
      </a>
      <div
        className="mx-auto flex items-center justify-between gap-4 md:gap-10 py-5 md:py-6"
        style={{ maxWidth: 1320, paddingLeft: "clamp(1rem, 3vw, 2.25rem)", paddingRight: "clamp(1rem, 3vw, 2.25rem)" }}
      >
        <Link to="/" className="focus:outline-none min-w-0" data-testid="header-home-link">
          <div className="flex items-baseline gap-5 min-w-0">
            <div
              className="site-name-responsive"
              style={{
                fontFamily: "'IBM Plex Sans', ui-sans-serif, system-ui, sans-serif",
                color: "#17233D",
                fontSize: "1.3rem",
                fontWeight: 600,
                letterSpacing: "-0.012em",
                lineHeight: 1.15,
              }}
              data-testid="site-name"
            >
              Financial Future Readiness Assessment
            </div>
            {showSubtitle && (
              <div
                className="hidden md:block"
                style={{
                  fontFamily: "'IBM Plex Sans', ui-sans-serif, system-ui, sans-serif",
                  color: "#8a97ac",
                  fontSize: "0.66rem",
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  fontWeight: 500,
                  lineHeight: 1.15,
                  transform: "translateY(-2px)",
                }}
                data-testid="site-descriptor"
              >
                A five-part planning assessment
              </div>
            )}
          </div>
        </Link>

        {showNav && (
          <>
            {/* Desktop nav — visible md+ */}
            <nav className="hidden md:flex items-center gap-8" data-testid="seo-nav" aria-label="Primary">
              <NavLink to="/assessment" style={linkStyle} data-testid="nav-assessment">Assessment</NavLink>
              <NavLink to="/how-it-works" style={linkStyle} data-testid="nav-how-it-works">How It Works</NavLink>
              <div
                className="relative"
                data-testid="nav-guides-root"
                ref={guidesRootRef}
              >
                <button
                  type="button"
                  id="nav-guides-button"
                  ref={guidesButtonRef}
                  className="nav-link inline-flex items-center gap-1"
                  style={{ ...guidesTriggerStyle, borderBottomColor: "transparent", paddingTop: 0, paddingLeft: 0, paddingRight: 0 }}
                  data-testid="nav-guides"
                  aria-haspopup="menu"
                  aria-expanded={guidesOpen}
                  aria-controls="guides-menu"
                  onClick={() => setGuidesOpen((v) => !v)}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowDown" && !guidesOpen) {
                      e.preventDefault();
                      setGuidesOpen(true);
                    }
                  }}
                >
                  <span>Guides</span>
                  <ChevronDown
                    aria-hidden="true"
                    style={{
                      display: "block", width: 14, height: 14, strokeWidth: 2,
                      transform: guidesOpen ? "rotate(180deg)" : "none",
                      transition: "transform 0.15s ease",
                    }}
                  />
                </button>
                <div
                  id="guides-menu"
                  role="menu"
                  aria-labelledby="nav-guides-button"
                  className="absolute right-0 mt-2 min-w-[280px] py-2 border border-[#e6edf5] shadow-sm bg-white z-40"
                  style={{
                    display: guidesOpen ? "block" : "none",
                  }}
                >
                  {GUIDES.map((g) => (
                    <NavLink
                      key={g.to}
                      to={g.to}
                      role="menuitem"
                      className="block px-4 py-2 text-sm hover:bg-[#F5F8FC]"
                      style={{ color: "#17233D" }}
                      data-testid={g.testId}
                      onClick={() => setGuidesOpen(false)}
                    >
                      {g.label}
                    </NavLink>
                  ))}
                </div>
              </div>
              <NavLink to="/financial-readiness-faq" style={linkStyle} data-testid="nav-faq">FAQ</NavLink>
            </nav>

            {/* Mobile hamburger — hidden md+ */}
            <button
              type="button"
              className="md:hidden inline-flex items-center justify-center"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              aria-controls="mobile-nav"
              onClick={() => setOpen((v) => !v)}
              style={{
                background: "transparent", border: 0, padding: 8,
                color: "#17233D", cursor: "pointer",
              }}
              data-testid="mobile-menu-toggle"
            >
              {open ? <X style={{ display: "block", width: 22, height: 22 }} /> : <Menu style={{ display: "block", width: 22, height: 22 }} />}
            </button>
          </>
        )}
      </div>

      {/* Mobile menu drawer (below md) */}
      {showNav && open && (
        <div
          id="mobile-nav"
          className="md:hidden"
          style={{ background: "#ffffff", borderTop: "1px solid #eef2f7" }}
          data-testid="mobile-nav"
        >
          <NavLink to="/assessment" className={mobileLinkClass} style={mobileLinkStyle} data-testid="mobile-nav-assessment">Assessment</NavLink>
          <NavLink to="/how-it-works" className={mobileLinkClass} style={mobileLinkStyle} data-testid="mobile-nav-how-it-works">How It Works</NavLink>
          <button
            type="button"
            className={mobileLinkClass + " w-full text-left flex items-center justify-between"}
            style={{ ...mobileLinkStyle, background: "transparent", border: 0, borderBottom: "1px solid #eef2f7", cursor: "pointer" }}
            onClick={() => setMGuidesOpen((v) => !v)}
            aria-expanded={mGuidesOpen}
            aria-controls="mobile-nav-guides-list"
            data-testid="mobile-nav-guides-toggle"
          >
            <span>Guides</span>
            <ChevronDown style={{ display: "block", width: 16, height: 16, transform: mGuidesOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s" }} />
          </button>
          {mGuidesOpen && (
            <div id="mobile-nav-guides-list" data-testid="mobile-nav-guides-list">
              {GUIDES.map((g) => (
                <NavLink
                  key={g.to}
                  to={g.to}
                  className={mobileLinkClass + " pl-8"}
                  style={mobileSubLinkStyle}
                  data-testid={g.testId.replace("nav-guide", "mobile-nav-guide")}
                >
                  {g.label}
                </NavLink>
              ))}
            </div>
          )}
          <NavLink to="/financial-readiness-faq" className={mobileLinkClass} style={mobileLinkStyle} data-testid="mobile-nav-faq">FAQ</NavLink>
        </div>
      )}
    </header>
  );
};

export default NeutralHeader;
